import React from 'react';
import { Box, Flex, SimpleGrid, Stack } from '@chakra-ui/react';
import tinycolor from 'tinycolor2';
import { useTranslation } from 'react-i18next';
import { ArticleCard } from '../molecules/ArticleCard';
import { ArticleCardAnalytics } from '../molecules/ArticleCardAnalytics';
import { CustomText } from '../atoms/CustomText';
// eslint-disable-next-line import/no-cycle
import { SectionIcons, useSettings } from '../../stateManagement/SettingsContext';
import { CustomFaqIcon } from '../atoms/CustomFaqIcon';
import { CustomPopularArticlesIcon } from '../atoms/CustomPopularArticlesIcon';

// Define the type for an article
export interface FaqArticle {
  category: string;
  title: string;
  id: string;
}

export interface PopularArticlesProps {
  faqArticles: FaqArticle[];
  bgColor?: string;
  faqArticlesTextColor: string;
  faqArticlesBgColor: string;
  secondaryTextColor: string;
  onClickPopularArticle: (id: string | number) => void;
  popularArticlesHeading: string;
  faqsHeading: string;
  primaryAccentColor?: string;
  secondaryAccentColor?: string;
  tertiaryAccentColor?: string;
  gradientAccentColor?: string;
  sectionIcons?: SectionIcons[];
  faqBodyCharLimit: number;
}

export const PopularArticlesPreview: React.FC<PopularArticlesProps> = ({
  faqArticles,
  bgColor,
  faqArticlesTextColor,
  faqArticlesBgColor,
  secondaryTextColor,
  onClickPopularArticle,
  popularArticlesHeading,
  faqsHeading,
  primaryAccentColor,
  secondaryAccentColor,
  tertiaryAccentColor = faqArticlesBgColor,
  gradientAccentColor,
  sectionIcons,
  faqBodyCharLimit,
}) => {
  const { popularArticlesData, faqs } = useSettings();
  const { i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const categoryTextColor = tinycolor(faqArticlesTextColor).lighten(30).toString();

  // Split faqArticles and popularArticlesData
  const firstThreeArticles = faqArticles.slice(0, 3);

  // Filter out duplicates
  const selectedIds = new Set(firstThreeArticles.map(article => article.id));
  const filteredPopularArticles = (popularArticlesData || []).filter(
    article => !selectedIds.has(article.id),
  );
  const nextThreeArticles = filteredPopularArticles.slice(0, 3);
  const translateArticles = (articles: any[]) =>
    articles.map(article => {
      const matchedFaq = faqs?.find(f => f.publish_id === article.id);

      const title =
        matchedFaq?.translations?.[currentLang]?.title ??
        matchedFaq?.translations?.en?.title ??
        article.title;

      const category =
        matchedFaq?.section_translations?.[currentLang]?.title ??
        matchedFaq?.section_translations?.en?.title ??
        article.category;

      const body =
        matchedFaq?.translations?.[currentLang]?.body ??
        matchedFaq?.translations?.en?.body ??
        article.body;
      const matchingIcon = sectionIcons?.find(icon => icon.name === category);

      return {
        ...article,
        title,
        category,
        body,
        matchingIcon,
      };
    });

  // client selected data
  const translatedTop3 = translateArticles(firstThreeArticles);

  // analytics data
  const translatedBottom3 = translateArticles(nextThreeArticles);

  return (
    <Box py={{ base: '4', md: '10vh' }} bg={bgColor}>
      <Flex direction={{ base: 'column', md: 'row' }} columnGap="18" rowGap="6">
        {/* Left: FAQs (70%) */}
        <Box flexBasis={{ base: '100%', md: '70%' }}>
          <CustomText
            variant="h1"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="bold"
            mb={{ base: '4', md: '6' }}
            color={secondaryTextColor}
            gap="1.5"
            display="inline-flex"
          >
            <Box as="span" display="inline-flex" alignItems="center" gap="2">
              <CustomFaqIcon size="1.7rem" color={primaryAccentColor} />
            </Box>
            {faqsHeading}
          </CustomText>
          <Box height="3px" width="60px" background={gradientAccentColor} />
          <Box height="1px" width="100%" background={gradientAccentColor} mb="6" />

          <Stack spacing="4">
            {translatedTop3.map((article, index) => (
              <Box key={article.id || `empty-top-${index}`}>
                {article.id ? (
                  <ArticleCard
                    title={article.title}
                    faqArticlesBgColor={faqArticlesBgColor}
                    faqArticlesTextColor={faqArticlesTextColor}
                    id={article.id}
                    onClickPopularArticle={onClickPopularArticle}
                    body={article.body}
                    faqBodyCharLimit={faqBodyCharLimit}
                  />
                ) : (
                  <Box
                    border="1px dashed #CBD5E0"
                    borderRadius="md"
                    height="100%"
                    minHeight="120px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    color="gray.400"
                  >
                    Empty Slot
                  </Box>
                )}
              </Box>
            ))}
          </Stack>
        </Box>

        {/* Right: Popular Articles (30%) */}
        <Box flexBasis={{ base: '100%', md: '30%' }}>
          <CustomText
            variant="h1"
            fontSize={{ base: '2xl', md: '3xl' }}
            fontWeight="bold"
            mb={{ base: '4', md: '6' }}
            color={secondaryTextColor}
            gap="1.5"
            display="inline-flex"
          >
            <Box as="span" display="inline-flex" alignItems="center">
              <CustomPopularArticlesIcon color={primaryAccentColor} size="1.7rem" />
            </Box>
            {popularArticlesHeading}
          </CustomText>
          <Box height="3px" width="60px" background={gradientAccentColor} />
          <Box height="1px" width="100%" background={gradientAccentColor} mb="6" />

          <SimpleGrid columns={1} spacing="4">
            {translatedBottom3.map(article => (
              <ArticleCardAnalytics
                key={article.id}
                title={article.title}
                category={article.category}
                icon={article.matchingIcon?.icon || ''}
                categoryTextColor={categoryTextColor}
                faqArticlesBgColor={tertiaryAccentColor}
                faqArticlesTextColor={faqArticlesTextColor}
                id={article.id}
                onClickPopularArticle={onClickPopularArticle}
                primaryAccentColor={primaryAccentColor}
                secondaryAccentColor={secondaryAccentColor}
              />
            ))}
          </SimpleGrid>
        </Box>
      </Flex>
    </Box>
  );
};
